import { useDroppable } from '@dnd-kit/core';
import React, { useEffect, useRef, useState } from 'react';

import * as styles from './calendar-scheduler.css';
import type { TimeSlotClickHandler } from './types';
import { formatDateKey, isSameDay } from './utils';

interface TimeGridProps {
    onTimeSlotClick?: TimeSlotClickHandler;
    onTimeSlotDoubleClick?: TimeSlotClickHandler;
    currentDate?: Date;
    scrollToTime?: Date;
    alwaysShowCurrentTime?: boolean;
    externalDropIdPrefix?: string;
    suppressSlotHover?: boolean;
    slotHeight?: number;
}

const DroppableTimeSlot: React.FC<{
    id: string;
    onClick: () => void;
    onDoubleClick?: () => void;
    className: string;
}> = ({ id, onClick, onDoubleClick, className }) => {
    const { setNodeRef } = useDroppable({ id });
    return (
        <div
            ref={setNodeRef}
            className={className}
            onClick={onClick}
            onDoubleClick={onDoubleClick}
        />
    );
};

export const TimeGrid: React.FC<TimeGridProps> = ({
    onTimeSlotClick,
    onTimeSlotDoubleClick,
    currentDate,
    scrollToTime,
    alwaysShowCurrentTime: _alwaysShowCurrentTime = false,
    externalDropIdPrefix,
    suppressSlotHover = false,
    slotHeight: slotHeightProp = 40,
}) => {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const [currentTime, setCurrentTime] = useState<Date>(new Date());
    const containerRef = useRef<HTMLDivElement>(null);
    const hasScrolledRef = useRef(false);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(new Date());
        }, 60000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        hasScrolledRef.current = false;
    }, [currentDate?.toDateString(), scrollToTime?.getTime()]);

    useEffect(() => {
        if (hasScrolledRef.current) return;
        if (!containerRef.current) return;

        const now = new Date();
        const isViewingToday = !currentDate || (
            currentDate.getDate() === now.getDate() &&
            currentDate.getMonth() === now.getMonth() &&
            currentDate.getFullYear() === now.getFullYear()
        );

        const slotHeight = slotHeightProp;
        let targetY: number | null = null;

        if (scrollToTime instanceof Date && !isNaN(scrollToTime.getTime())) {
            const scrollHour = scrollToTime.getHours();
            const scrollMinute = scrollToTime.getMinutes();
            targetY = (scrollHour + scrollMinute / 60) * slotHeight;
        } else if (isViewingToday) {
            const currentHour = now.getHours();
            const currentMinute = now.getMinutes();
            targetY = (currentHour + currentMinute / 60) * slotHeight;
        }

        if (targetY !== null) {
            const scrollableParent = containerRef.current.closest(`.${styles.calendarGrid}`);
            if (scrollableParent) {
                const topOffset = slotHeight * 1.5;
                const scrollPosition = Math.max(0, targetY - topOffset);

                requestAnimationFrame(() => {
                    scrollableParent.scrollTop = scrollPosition;
                });
                hasScrolledRef.current = true;
            }
        }
    }, [currentDate, scrollToTime, slotHeightProp]);

    const getCurrentTimePosition = () => {
        const now = currentTime;
        const hours = now.getHours();
        const minutes = now.getMinutes();
        return hours * 60 + minutes;
    };

    const formatHour = (hour: number) => {
        if (hour === 0) return '12 AM';
        if (hour === 12) return '12 PM';
        if (hour > 12) return `${hour - 12} PM`;
        return `${hour} AM`;
    };

    // Check if we're viewing today
    const isToday = () => {
        if (!currentDate) return true;
        const today = new Date();
        return (
            currentDate.getDate() === today.getDate() &&
            currentDate.getMonth() === today.getMonth() &&
            currentDate.getFullYear() === today.getFullYear()
        );
    };

    const currentTimePosition = getCurrentTimePosition();
    const currentTimeStyle = {
        top: `${(currentTimePosition / (24 * 60)) * 100}%`,
    };

    const dropDate = currentDate && !isNaN(currentDate.getTime()) ? currentDate : new Date();
    const dropDateStr = externalDropIdPrefix ? formatDateKey(dropDate) : '';

    const slotClassName = suppressSlotHover
        ? `${styles.timeSlotClickable} ${styles.timeSlotClickableNoHover}`
        : styles.timeSlotClickable;

    const renderSlot = (hour: number, minute: number) => {
        const handleClick = () => onTimeSlotClick?.(hour, minute);
        const handleDoubleClick = () =>
            onTimeSlotDoubleClick?.(hour, minute);
        if (!externalDropIdPrefix) {
            return (
                <div
                    className={slotClassName}
                    onClick={handleClick}
                    onDoubleClick={handleDoubleClick}
                />
            );
        }

        const slotId = `${externalDropIdPrefix}${dropDateStr}:${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        return (
            <DroppableTimeSlot
                id={slotId}
                onClick={handleClick}
                onDoubleClick={handleDoubleClick}
                className={slotClassName}
            />
        );
    };

    return (
        <div ref={containerRef} className={styles.timeGridContainer}>
            {/* Time sidebar */}
            <div className={styles.timeSidebar}>
                {hours.map(hour => (
                    <div key={hour} className={styles.timeSlot}>
                        <span className={styles.timeLabel}>
                            {formatHour(hour)}
                        </span>
                    </div>
                ))}
            </div>

            {/* Main grid content */}
            <div
                className={styles.timeGridContent}
                data-calendar-grid="true"
            >
                {hours.map(hour => (
                    <React.Fragment key={hour}>
                        {/* First half hour */}
                        {renderSlot(hour, 0)}
                        {/* Second half hour */}
                        {renderSlot(hour, 30)}
                    </React.Fragment>
                ))}
            </div>

            {/* Current time indicator */}
            {isToday() && (
                <div
                    className={styles.currentTimeIndicator}
                    style={currentTimeStyle}
                >
                    <div className={styles.currentTimeDot} />
                    <div className={styles.currentTimeLine} />
                </div>
            )}
        </div>
    );
};

interface WeekTimeGridProps {
    onTimeSlotClick?: (date: Date, hour: number, minute: number) => void;
    onTimeSlotDoubleClick?: (date: Date, hour: number, minute: number) => void;
    weekDates: Date[];
    currentDate?: Date;
    scrollToTime?: Date;
    alwaysShowCurrentTime?: boolean;
    externalDropIdPrefix?: string;
    suppressSlotHover?: boolean;
    slotHeight?: number;
}

export const WeekTimeGrid: React.FC<WeekTimeGridProps> = ({
    onTimeSlotClick,
    onTimeSlotDoubleClick,
    weekDates,
    currentDate,
    scrollToTime,
    alwaysShowCurrentTime: _alwaysShowCurrentTime = false,
    externalDropIdPrefix,
    suppressSlotHover = false,
    slotHeight: slotHeightProp = 40,
}) => {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const [currentTime, setCurrentTime] = useState<Date>(new Date());
    const containerRef = useRef<HTMLDivElement>(null);
    const hasScrolledRef = useRef(false);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(new Date());
        }, 60000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        hasScrolledRef.current = false;
    }, [currentDate?.toDateString(), scrollToTime?.getTime()]);

    useEffect(() => {
        if (hasScrolledRef.current) return;
        if (!containerRef.current) return;

        const now = new Date();
        const isViewingToday = weekDates.some(date => isSameDay(date, now));

        const slotHeight = slotHeightProp;
        let targetY: number | null = null;

        if (scrollToTime instanceof Date && !isNaN(scrollToTime.getTime())) {
            const scrollHour = scrollToTime.getHours();
            const scrollMinute = scrollToTime.getMinutes();
            targetY = (scrollHour + scrollMinute / 60) * slotHeight;
        } else if (isViewingToday) {
            const currentHour = now.getHours();
            const currentMinute = now.getMinutes();
            targetY = (currentHour + currentMinute / 60) * slotHeight;
        }

        if (targetY !== null) {
            const scrollableParent = containerRef.current.closest(`.${styles.calendarGrid}`);
            if (scrollableParent) {
                const topOffset = slotHeight * 1.5;
                const scrollPosition = Math.max(0, targetY - topOffset);

                requestAnimationFrame(() => {
                    scrollableParent.scrollTop = scrollPosition;
                });
                hasScrolledRef.current = true;
            }
        }
    }, [currentDate, scrollToTime, weekDates, slotHeightProp]);

    const getCurrentTimePosition = () => {
        const now = currentTime;
        const hours = now.getHours();
        const minutes = now.getMinutes();
        return hours * 60 + minutes;
    };

    const formatHour = (hour: number) => {
        if (hour === 0) return '12 AM';
        if (hour === 12) return '12 PM';
        if (hour > 12) return `${hour - 12} PM`;
        return `${hour} AM`;
    };

    const shouldShowCurrentTime = () => {
        const now = new Date();
        return weekDates.some(date => isSameDay(date, now));
    };

    const currentTimePosition = getCurrentTimePosition();
    const currentTimeStyle = {
        top: `${(currentTimePosition / (24 * 60)) * 100}%`,
    };

    const slotClassName = suppressSlotHover
        ? `${styles.timeSlotClickable} ${styles.timeSlotClickableNoHover}`
        : styles.timeSlotClickable;

    const renderSlot = (date: Date, hour: number, minute: number) => {
        const handleClick = () => onTimeSlotClick?.(date, hour, minute);
        const handleDoubleClick = () =>
            onTimeSlotDoubleClick?.(date, hour, minute);
        if (!externalDropIdPrefix) {
            return (
                <div
                    className={slotClassName}
                    onClick={handleClick}
                    onDoubleClick={handleDoubleClick}
                />
            );
        }

        const slotId = `${externalDropIdPrefix}${formatDateKey(date)}:${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        return (
            <DroppableTimeSlot
                id={slotId}
                onClick={handleClick}
                onDoubleClick={handleDoubleClick}
                className={slotClassName}
            />
        );
    };

    return (
        <div ref={containerRef} className={styles.weekGridContainer}>
            <div className={styles.weekHeader}>
                <div className={styles.weekHeaderSpacer} />
                {weekDates.map(date => {
                    const isToday = isSameDay(date, new Date());
                    return (
                        <div
                            key={formatDateKey(date)}
                            className={`${styles.weekHeaderCell} ${isToday ? styles.weekHeaderCellToday : ''}`}
                        >
                            <span className={styles.weekHeaderDay}>
                                {new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(date)}
                            </span>
                            <span className={styles.weekHeaderDate}>
                                {new Intl.DateTimeFormat('en-US', { day: 'numeric' }).format(date)}
                            </span>
                        </div>
                    );
                })}
            </div>

            <div className={styles.weekBody}>
                <div className={styles.timeSidebar}>
                    {hours.map(hour => (
                        <div key={hour} className={styles.timeSlot}>
                            <span className={styles.timeLabel}>
                                {formatHour(hour)}
                            </span>
                        </div>
                    ))}
                </div>

                <div className={styles.weekColumns} data-calendar-grid="true">
                    {weekDates.map(date => (
                        <div
                            key={formatDateKey(date)}
                            className={styles.weekDayColumn}
                            data-today={isSameDay(date, new Date()) || undefined}
                        >
                            {hours.map(hour => (
                                <React.Fragment key={hour}>
                                    {renderSlot(date, hour, 0)}
                                    {renderSlot(date, hour, 30)}
                                </React.Fragment>
                            ))}
                        </div>
                    ))}
                </div>

                {shouldShowCurrentTime() && (
                    <div
                        className={styles.weekCurrentTimeIndicator}
                        style={currentTimeStyle}
                    >
                        <div className={styles.currentTimeDot} />
                        <div className={styles.currentTimeLine} />
                    </div>
                )}
            </div>
        </div>
    );
};
